import {
  useEffect,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import styled from "styled-components";

import { ChevronIcon } from "../../icons/glyphs";
import { interactiveSurfaceCss } from "../foundations/interactiveSurface";
import { MEDIA_TIMELINE_POLICY } from "./mediaTimelineMath";
import type { TimelineNavigationRegion } from "./timelineTrackLayout";

const TIMELINE_EDGE_INDICATOR_WIDTH = `${
  MEDIA_TIMELINE_POLICY.edgeIndicatorWidthPx / MEDIA_TIMELINE_POLICY.rootFontBasisPx
}rem`;

const NavigationLayer = styled.div`
  position: absolute;
  /* Above passive row paint, below marker actions (3), the playhead cursor (5), selection
     handles (7/8), and row-local playhead lines (9). */
  z-index: 2;
  inset: 0;
  pointer-events: none;
`;

const EdgeIndicator = styled.button<{ $side: "left" | "right" }>`
  position: absolute;
  top: 0;
  ${({ $side }) => ($side === "left" ? "left: 0;" : "right: 0;")}
  display: inline-flex;
  width: ${TIMELINE_EDGE_INDICATOR_WIDTH};
  height: 100%;
  align-items: center;
  justify-content: ${({ $side }) => ($side === "left" ? "flex-start" : "flex-end")};
  padding: 0 0.25rem;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.title};
  line-height: 1;
  pointer-events: auto;
  touch-action: none;
  --c6-media-edge-fade-surface: ${({ theme }) => theme.color.bg.surface};

  &::before {
    position: absolute;
    z-index: 0;
    inset: 0;
    background: linear-gradient(
      to ${({ $side }) => ($side === "left" ? "right" : "left")},
      var(--c6-media-edge-fade-surface),
      transparent
    );
    content: "";
    pointer-events: none;
  }

  svg {
    position: relative;
    z-index: 1;
    transform: rotate(${({ $side }) => ($side === "left" ? "90deg" : "-90deg")});
  }

  ${({ theme }) =>
    interactiveSurfaceCss({
      rest: {
        background: "transparent",
        border: "transparent",
        foreground: theme.color.text.default,
      },
      hover: {
        background: "transparent",
        border: "transparent",
        foreground: theme.color.text.accent,
      },
      active: {
        background: "transparent",
        border: "transparent",
        foreground: theme.color.text.accent,
      },
      disabled: {
        background: "transparent",
        border: "transparent",
        foreground: theme.color.text.disabled,
      },
    })}

  &:not(:disabled):hover {
    --c6-media-edge-fade-surface: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  }

  &:not(:disabled):active {
    --c6-media-edge-fade-surface: ${({ theme }) => theme.color.bg.surfaceOverlay};
  }

  /* The full-height edge button needs an inset ring to remain inside the viewport overlay. */
  &:focus-visible {
    outline-offset: -2px;
  }
`;

interface TimelineViewportNavigationProps {
  region?: TimelineNavigationRegion;
  canPanLeft: boolean;
  canPanRight: boolean;
  disabled: boolean;
  previousLabel: string;
  nextLabel: string;
  onPanStart: (direction: -1 | 1) => void;
  onPanStop: () => void;
  onPanStep: (direction: -1 | 1) => void;
  onKeyboardPan: (source: HTMLButtonElement) => void;
  onPointerPan: () => void;
}

/** Shared pan commands painted over one eligible run, or in the separate footer. */
export function TimelineViewportNavigation({
  region,
  canPanLeft,
  canPanRight,
  disabled,
  previousLabel,
  nextLabel,
  onPanStart,
  onPanStop,
  onPanStep,
  onKeyboardPan,
  onPointerPan,
}: TimelineViewportNavigationProps) {
  // A consumer can remove an overlay run during a held gesture; do not leave its pan loop alive.
  useEffect(() => onPanStop, [onPanStop]);
  const finishPointerPan = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onPanStop();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const renderEdge = (side: "left" | "right", label: string) => {
    const direction = side === "left" ? -1 : 1;
    return (
      <EdgeIndicator
        type="button"
        $side={side}
        aria-label={label}
        data-media-timeline-edge={side}
        disabled={disabled}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget.setPointerCapture(event.pointerId);
          onPanStart(direction);
          onPointerPan();
        }}
        onPointerUp={finishPointerPan}
        onPointerCancel={finishPointerPan}
        onLostPointerCapture={onPanStop}
        onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          if (event.detail !== 0) return;
          onPanStep(direction);
          onKeyboardPan(event.currentTarget);
        }}
      >
        <ChevronIcon />
      </EdgeIndicator>
    );
  };

  if (!canPanLeft && !canPanRight) return null;

  return (
    <NavigationLayer
      data-media-timeline-viewport-navigation="true"
      data-media-navigation-placement={region ? "overlay" : "footer"}
      data-media-navigation-start={region?.firstTrackId}
      data-media-navigation-end={region?.lastTrackId}
      role={region ? "group" : undefined}
      aria-label={region?.label}
      style={
        region
          ? {
              gridRow: `${region.firstIndex + 1} / ${region.lastIndex + 2}`,
              top: region.gapBefore ? "0.35rem" : 0,
            }
          : undefined
      }
    >
      {canPanLeft ? renderEdge("left", previousLabel) : null}
      {canPanRight ? renderEdge("right", nextLabel) : null}
    </NavigationLayer>
  );
}
